import { StatusBar } from "expo-status-bar";
import {
  Button,
  Pressable,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useState, useCallback, useRef, useMemo } from "react";

export default function App() {
  const [nonce, setNonce] = useState(0);
  const resetSheet = useCallback(() => setNonce(nonce + 1), [nonce, setNonce]);

  return <SampleSheet key={nonce} reset={resetSheet} />;
}

function SampleSheet(props: { reset: () => void }) {
  const [total, setTotal] = useState(30);
  const [isComplete, setIsComplete] = useState(false);

  const categoryValuesRef = useRef<Record<string, number>>({
    Uniformity: 10,
    "Clean Cup": 10,
    Sweetness: 10,
    Defects: 0,
  });
  const setValueForCategory = useCallback(
    (name: string, value: number) => {
      const curVal = categoryValuesRef.current[name];
      if (value === curVal) {
        return;
      }
      categoryValuesRef.current[name] = value;
      const newTotal = Object.values(categoryValuesRef.current).reduce(
        (curr, next) => (curr || 0) + (next || 0)
      );
      setTotal(newTotal);
      setIsComplete(
        Object.values(categoryValuesRef.current).filter((val) => !isNaN(val))
          .length === 11
      );
    },
    [setTotal, categoryValuesRef]
  );
  const motd = useMemo(() => {
    const messages = [
      "😎 Don't Overthink It 🏝️",
      "🤘 Good Cuppers Are Confident 🎸",
      "☕️ This Should Be Fun ❤️",
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  }, []);

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar style="auto" />
      <ScrollView style={{ flexDirection: "column" }}>
        <InfoSection motd={motd} />
        <Category
          name="Fragrance/Aroma"
          setValueForCategory={setValueForCategory}
          categoryValues={categoryValuesRef.current}
          scoringType="quality"
        />
        <Category
          name="Flavor"
          setValueForCategory={setValueForCategory}
          categoryValues={categoryValuesRef.current}
          scoringType="quality"
        />
        <Category
          name="Aftertaste"
          setValueForCategory={setValueForCategory}
          categoryValues={categoryValuesRef.current}
          scoringType="quality"
        />
        <Category
          name="Acidity"
          setValueForCategory={setValueForCategory}
          categoryValues={categoryValuesRef.current}
          scoringType="quality"
        />
        <Category
          name="Body"
          setValueForCategory={setValueForCategory}
          categoryValues={categoryValuesRef.current}
          scoringType="quality"
        />
        <Category
          name="Uniformity"
          setValueForCategory={setValueForCategory}
          categoryValues={categoryValuesRef.current}
          scoringType="technical"
        />
        <Category
          name="Balance"
          setValueForCategory={setValueForCategory}
          categoryValues={categoryValuesRef.current}
          scoringType="quality"
        />
        <Category
          name="Clean Cup"
          setValueForCategory={setValueForCategory}
          categoryValues={categoryValuesRef.current}
          scoringType="technical"
        />
        <Category
          name="Sweetness"
          setValueForCategory={setValueForCategory}
          categoryValues={categoryValuesRef.current}
          scoringType="technical"
        />
        <Category
          name="Overall"
          setValueForCategory={setValueForCategory}
          categoryValues={categoryValuesRef.current}
          scoringType="quality"
        />
        <Category
          name="Defects"
          setValueForCategory={setValueForCategory}
          categoryValues={categoryValuesRef.current}
          scoringType="defects"
        />
        <View style={{ height: 200 }} />
      </ScrollView>
      <Header
        total={total}
        isComplete={isComplete}
        reset={props.reset}
        categoryValues={categoryValuesRef.current}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#fff",
    alignItems: "stretch",
    justifyContent: "flex-start",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
});

function InfoSection(props: { motd: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpansion = useCallback(
    () => setIsExpanded(!isExpanded),
    [isExpanded, setIsExpanded]
  );
  return (
    <Pressable
      style={{ padding: 24 }}
      onPress={toggleExpansion}
    >
      <Text
        style={{
          textAlign: "center",
          fontSize: 18,
          marginBottom: 8,
          fontWeight: "600",
        }}
      >
        {props.motd}
      </Text>
      <Text
        style={{
          textAlign: "center",
          fontSize: 12,
          color: "darkgray",
          fontWeight: "600",
        }}
      >
        Usage Tips {isExpanded ? "↑" : "↓"}
      </Text>
      {isExpanded && (
        <View style={{ flexDirection: "column", marginTop: 8 }}>
          <BulletPoint bullet="•">
            Tap the score menu on the bottom of the screen to expand or collapse
            it. The score menu breaks out the subtotal and defect penalty from
            the total score.
          </BulletPoint>
          <BulletPoint bullet="•">
            The score menu is yellow and has a ⚠️ if there are any unfilled
            attributes. It turns green when everything is filled in. Expand the
            score menu to see the reset button.
          </BulletPoint>
          <BulletPoint bullet="•">
            You can tap a digit twice instead of pressing '.00' to move on. Fields
            autocollapse when filled. Tap any attribute to open them for editing.
          </BulletPoint>
          <BulletPoint bullet="•">
            Tap Uniformity, Clean Cup, or Sweetness to mark a cup as failing the
            standard ('❌'). Tap Defects to record the number and intensity. Technical
            categories can't be collapsed if any penalties are marked.
          </BulletPoint>
        </View>
      )}
    </Pressable>
  );
}

function BulletPoint(props: { bullet: string; children: string }) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "flex-start",
        marginBottom: 8,
      }}
    >
      <View>
        <Text>{props.bullet} </Text>
      </View>
      <Text>{props.children}</Text>
    </View>
  );
}

function Header(props: {
  total: number;
  isComplete: boolean;
  reset: () => void;
  categoryValues: Record<string, number>;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpansion = useCallback(
    () => setIsExpanded(!isExpanded),
    [isExpanded, setIsExpanded]
  );

  return (
    <Pressable
      onPress={toggleExpansion}
      style={{
        ...headerStyles.headerContainer,
        backgroundColor: props.isComplete ? "lightgreen" : "lightyellow",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <Text style={headerStyles.caret}>{isExpanded ? "🔽" : "🔼"}</Text>
        <Text style={headerStyles.totalLabel}>Total</Text>
        <Text style={headerStyles.scoreText}>
          {props.isComplete ? "✅" : "⚠️"}{" "}
          {props.total.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Text>
      </View>
      {isExpanded && (
        <>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <Text style={headerStyles.totalLabel}>Subtotal</Text>
            <Text style={headerStyles.scoreText}>
              {props.isComplete ? "" : "⚠️"}{" "}
              {(props.total - props.categoryValues["Defects"]).toLocaleString(
                undefined,
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }
              )}
            </Text>
          </View>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <Text style={headerStyles.totalLabel}>Defects</Text>
            <Text style={headerStyles.scoreText}>
              {props.categoryValues["Defects"].toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Text>
          </View>
          <View style={{ marginVertical: 8 }}>
            <Text style={{ fontWeight: "500", marginBottom: 4 }}>
              Reminders
            </Text>
            <Text>✔️ Mark the roast level and all four intensities.</Text>
            <Text>✔️ Name your defects.</Text>
            <Text>✔️ Fill in the subtotal box.</Text>
          </View>
          <View style={{ padding: 24 }}>
            <Button title="Reset" onPress={props.reset} color="red" />
          </View>
        </>
      )}
    </Pressable>
  );
}

const headerStyles = StyleSheet.create({
  headerContainer: {
    backgroundColor: "darkgray",
    padding: 12,
    paddingTop: 24,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "#424242",
    position: "absolute",
    right: 8,
    left: 8,
    bottom: 0,
  },
  totalLabel: {
    fontSize: 18,
    marginRight: 12,
    fontWeight: "bold",
  },
  scoreText: {
    fontWeight: "bold",
    fontSize: 24,
  },
  caret: {
    fontSize: 24,
    fontWeight: "600",
    width: 64,
    marginRight: 12,
  },
});

function Category(props: {
  name: string;
  setValueForCategory: (category: string, value: number) => void;
  categoryValues: Readonly<Record<string, number | undefined>>;
  scoringType: "quality" | "technical" | "defects";
  icon?: string;
}) {
  const setScore = useCallback(
    (value: number) => {
      props.setValueForCategory(props.name, value);
    },
    [props.setValueForCategory]
  );
  const score = props.categoryValues[props.name];
  const [isExpanded, setIsExpanded] = useState(score == null);
  const collapse = useCallback(() => setIsExpanded(false), [setIsExpanded]);
  const toggle = useCallback(() => {
    if (!isExpanded) {
      setIsExpanded(true);
      return;
    }
    switch (props.scoringType) {
      case "technical":
        if (score === 10) {
          setIsExpanded(false);
        }
        return;
      case "quality":
        if (score != null) {
          setIsExpanded(false);
        }
        return;
      case "defects":
        if (score === 0) setIsExpanded(false);
        return;
    }
  }, [setIsExpanded, isExpanded, props.scoringType, score]);
  const scoringComponent = useMemo(() => {
    switch (props.scoringType) {
      case "quality":
        return (
          <QualityScorePicker
            value={score}
            setScore={setScore}
            onFinishedSetting={collapse}
          />
        );
      case "technical":
        return <TechnicalScorePicker setScore={setScore} />;
      case "defects":
        return <DefectScorePicker setScore={setScore} />;
    }
  }, [props.scoringType, score, setScore, collapse]);

  return (
    <View style={categoryStyles.container}>
      <Pressable style={categoryStyles.rowContainer} onPress={toggle}>
        <Text style={categoryStyles.labelText}>
          {props.icon}
          {props.name}
        </Text>
        <Text style={categoryStyles.scoreText}>
          {score == null && "⚠️ "}
          {(score ?? 0).toLocaleString(undefined, {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}
        </Text>
      </Pressable>
      {isExpanded && <View style={{ marginTop: 24 }}>{scoringComponent}</View>}
    </View>
  );
}

const categoryStyles = StyleSheet.create({
  container: {
    flexDirection: "column",
    backgroundColor: "lightgray",
    marginBottom: 8,
    padding: 24,
  },
  rowContainer: {
    flexDirection: "row",
  },
  labelText: {
    fontSize: 18,
    fontWeight: "600",
    flexGrow: 1,
    marginRight: 24,
  },
  scoreText: {
    fontSize: 24,
    fontWeight: "bold",
  },
});

function TechnicalScorePicker(props: { setScore: (value: number) => void }) {
  const [markedCups, setMarkedCups] = useState<
    [boolean, boolean, boolean, boolean, boolean]
  >([false, false, false, false, false]);
  const setValueForCup = useCallback(
    (cupNumber: number, value: boolean) => {
      if (cupNumber < 0 || cupNumber >= 5) {
        return;
      }
      let newMarkedCups: [boolean, boolean, boolean, boolean, boolean] = [
        ...markedCups,
      ];
      newMarkedCups[cupNumber] = value;
      setMarkedCups(newMarkedCups);
      props.setScore(newMarkedCups.filter((x) => !x).length * 2);
    },
    [markedCups, setMarkedCups, props.setScore]
  );

  return (
    <View style={qualityScoreStyles.container}>
      <View style={qualityScoreStyles.optionRow}>
        <QualityScoreOptionTile
          value={!markedCups[0]}
          label={markedCups[0] ? "❌" : "✅"}
          setValue={(v: boolean) => setValueForCup(0, v)}
          isSelected={false}
        />
        <QualityScoreOptionTile
          value={!markedCups[1]}
          label={markedCups[1] ? "❌" : "✅"}
          setValue={(v: boolean) => setValueForCup(1, v)}
          isSelected={false}
        />
        <QualityScoreOptionTile
          value={!markedCups[2]}
          label={markedCups[2] ? "❌" : "✅"}
          setValue={(v: boolean) => setValueForCup(2, v)}
          isSelected={false}
        />
        <QualityScoreOptionTile
          value={!markedCups[3]}
          label={markedCups[3] ? "❌" : "✅"}
          setValue={(v: boolean) => setValueForCup(3, v)}
          isSelected={false}
        />
        <QualityScoreOptionTile
          value={!markedCups[4]}
          label={markedCups[4] ? "❌" : "✅"}
          setValue={(v: boolean) => setValueForCup(4, v)}
          isSelected={false}
        />
      </View>
    </View>
  );
}

function DefectScorePicker(props: { setScore: (value: number) => void }) {
  const [numCups, setNumCups] = useState(0);
  const [intensity, setIntensity] = useState(-2);

  const setNumCupsAndUpdateScore = useCallback(
    (value: number) => {
      setNumCups(value);
      props.setScore(value * intensity);
    },
    [setNumCups, props.setScore, intensity]
  );

  const setIntensityAndUpdateScore = useCallback(
    (value: number) => {
      setIntensity(value);
      props.setScore(numCups * value);
    },
    [setIntensity, props.setScore, numCups]
  );

  return (
    <View style={qualityScoreStyles.container}>
      <View style={qualityScoreStyles.optionRow}>
        <QualityScoreOptionTile
          value={0}
          setValue={setNumCupsAndUpdateScore}
          isSelected={numCups === 0}
        />
        <QualityScoreOptionTile
          value={1}
          setValue={setNumCupsAndUpdateScore}
          isSelected={numCups === 1}
        />
        <QualityScoreOptionTile
          value={2}
          setValue={setNumCupsAndUpdateScore}
          isSelected={numCups === 2}
        />
        <QualityScoreOptionTile
          value={3}
          setValue={setNumCupsAndUpdateScore}
          isSelected={numCups === 3}
        />
        <QualityScoreOptionTile
          value={4}
          setValue={setNumCupsAndUpdateScore}
          isSelected={numCups === 4}
        />
        <QualityScoreOptionTile
          value={5}
          setValue={setNumCupsAndUpdateScore}
          isSelected={numCups === 5}
        />
      </View>
      <View style={qualityScoreStyles.optionRow}>
        <QualityScoreOptionTile
          value={-2}
          label="Taint (-2)"
          setValue={setIntensityAndUpdateScore}
          isSelected={intensity === -2}
        />
        <QualityScoreOptionTile
          value={-4}
          label="Fault (-4)"
          setValue={setIntensityAndUpdateScore}
          isSelected={intensity === -4}
        />
      </View>
    </View>
  );
}

function QualityScorePicker(props: {
  value: number | undefined;
  setScore: (value: number) => void;
  onFinishedSetting: () => void;
}) {
  const [wholeDigit, setWholeDigit] = useState(
    props.value == null ? undefined : Math.floor(props.value)
  );
  const [decimalPart, setDecimalPart] = useState(
    props.value == null ? undefined : props.value % 1
  );

  const setWholeDigitAndUpdateScore = useCallback(
    (value: number) => {
      if (wholeDigit === value && decimalPart == null) {
        setDecimalPart(0);
        props.setScore(value);
        props.onFinishedSetting();
      }
      setWholeDigit(value);
      setDecimalPart(undefined);
      props.setScore(value);
    },
    [setWholeDigit, setDecimalPart, wholeDigit, decimalPart, props.setScore]
  );

  const setDecimalPartAndUpdateScore = useCallback(
    (value: number) => {
      if (wholeDigit != null) {
        setDecimalPart(value);
        props.onFinishedSetting();
        props.setScore(wholeDigit + value);
      }
    },
    [setWholeDigit, props.onFinishedSetting, wholeDigit, props.setScore]
  );

  return (
    <View style={qualityScoreStyles.container}>
      <View style={qualityScoreStyles.optionRow}>
        <QualityScoreOptionTile
          value={6}
          setValue={setWholeDigitAndUpdateScore}
          isSelected={wholeDigit === 6}
        />
        <QualityScoreOptionTile
          value={7}
          setValue={setWholeDigitAndUpdateScore}
          isSelected={wholeDigit === 7}
        />
        <QualityScoreOptionTile
          value={8}
          setValue={setWholeDigitAndUpdateScore}
          isSelected={wholeDigit === 8}
        />
        <QualityScoreOptionTile
          value={9}
          setValue={setWholeDigitAndUpdateScore}
          isSelected={wholeDigit === 9}
        />
      </View>
      <View style={qualityScoreStyles.optionRow}>
        <QualityScoreOptionTile
          value={0.0}
          label=".00"
          setValue={setDecimalPartAndUpdateScore}
          isSelected={decimalPart === 0}
          disabled={wholeDigit == null}
        />
        <QualityScoreOptionTile
          value={0.25}
          label=".25"
          setValue={setDecimalPartAndUpdateScore}
          isSelected={decimalPart === 0.25}
          disabled={wholeDigit == null}
        />
        <QualityScoreOptionTile
          value={0.5}
          label=".50"
          setValue={setDecimalPartAndUpdateScore}
          isSelected={decimalPart === 0.5}
          disabled={wholeDigit == null}
        />
        <QualityScoreOptionTile
          value={0.75}
          label=".75"
          setValue={setDecimalPartAndUpdateScore}
          isSelected={decimalPart === 0.75}
          disabled={wholeDigit == null}
        />
      </View>
    </View>
  );
}

function QualityScoreOptionTile<T extends { toString: () => string }>(props: {
  value: T;
  label?: string | T | undefined;
  setValue: (value: T) => void;
  isSelected: boolean;
  disabled?: boolean;
}) {
  const value = props.value;
  const label = props.label ?? value.toString();
  const onPress = useCallback(
    () => props.setValue(props.value),
    [props.setValue, props.value]
  );

  return (
    <Pressable
      style={{
        ...qualityScoreStyles.optionTile,
        ...(props.isSelected
          ? qualityScoreStyles.optionTileSelected
          : props.disabled
          ? qualityScoreStyles.optionTileDisabled
          : null),
      }}
      onPress={onPress}
    >
      <Text
        style={
          props.disabled
            ? { ...qualityScoreStyles.optionTileText, color: "lightgray" }
            : qualityScoreStyles.optionTileText
        }
      >
        {label.toString()}
      </Text>
    </Pressable>
  );
}

const qualityScoreStyles = StyleSheet.create({
  container: {
    flexDirection: "column",
    flexGrow: 1,
  },
  optionRow: {
    flexDirection: "row",
    alignItems: "stretch",
  },
  optionTile: {
    margin: 8,
    padding: 12,
    backgroundColor: "white",
    alignItems: "center",
    justifyContent: "center",
    flexGrow: 1,
  },
  optionTileSelected: {
    backgroundColor: "cyan",
  },
  optionTileDisabled: {
    backgroundColor: "darkgray",
  },
  optionTileText: {
    fontWeight: "600",
    fontSize: 18,
  },
});
